import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Layers, Search, MapPin, RefreshCw, AlertCircle, Loader, Play, ClipboardList
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { LocationSearchModal } from '../../../common/LocationSearchModal';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { consolidationService } from '../../../../services/consolidationService';
import {
    ConsolidationAnalysisResponse,
    ConsolidationExecuteLine,
    ConsolidationExecuteResponse,
    ConsolidationExecution,
    ConsolidationKpi,
    ConsolidationMode,
    ConsolidationObjective
} from '../../../../types/consolidation';
import { SuggestionTable, suggestionKey } from './components/SuggestionTable';
import { PlanNameModal } from './components/PlanNameModal';
import { ExecuteResultPanel } from './components/ExecuteResultPanel';

interface ConsolidationPlanningAppProps {
    onBack: () => void;
}

/**
 * Lagersjef-verktøy «Konsolidering» — analyserer fragmentert saldo og
 * foreslår sammenslåing av bins. Tre moduser (fragmenterte varer /
 * frigjør lokasjoner / destinasjon-først). Utførelse delegeres server-side
 * til heatmap-pipelinen (immediate) eller MoveProcess (plan).
 */
export const ConsolidationPlanningApp: React.FC<ConsolidationPlanningAppProps> = ({ onBack }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { currentWarehouse, warehouses } = useWarehouse();

    const warehouseID = currentWarehouse || '1';
    const companyNo = user?.companyNo || '';

    // Konsolidering er kun for WMS-utgaven (Compass er master på saldo/lokasjon).
    // GO-lagre (useAWYesNo=false) har ERP som master og mangler dessuten
    // StcBin-/ItemBinSize-grunnlaget forslagsmotoren bygger på.
    const isAdvancedWarehouse = warehouses.find(w => w.warehouseID === currentWarehouse)?.useAWYesNo ?? false;

    // KPI
    const [kpi, setKpi] = useState<ConsolidationKpi | null>(null);
    const [kpiLoading, setKpiLoading] = useState(false);

    // Modus + filtre
    const [mode, setMode] = useState<ConsolidationMode>('item');
    const [targetBinCode, setTargetBinCode] = useState('');
    const [showLocationSearch, setShowLocationSearch] = useState(false);
    const [objective, setObjective] = useState<ConsolidationObjective>('maxFreed');
    const [locationPrefix, setLocationPrefix] = useState('');
    const [topN, setTopN] = useState(50);
    const [includeEstimatedCapacity, setIncludeEstimatedCapacity] = useState(false);

    // Analyse
    const [analyzing, setAnalyzing] = useState(false);
    const [analysis, setAnalysis] = useState<ConsolidationAnalysisResponse | null>(null);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

    // Utførelse
    const [executing, setExecuting] = useState(false);
    const [executeResult, setExecuteResult] = useState<ConsolidationExecuteResponse | null>(null);
    const [showPlanModal, setShowPlanModal] = useState(false);

    const [error, setError] = useState<string | null>(null);

    const loadKpi = useCallback(async () => {
        setKpiLoading(true);
        try {
            const data = await consolidationService.getKpi(warehouseID);
            setKpi(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : t('manager.modules.consolidation.errors.kpi', {}, 'Kunne ikke hente nøkkeltall'));
        } finally {
            setKpiLoading(false);
        }
    }, [warehouseID, t]);

    useEffect(() => {
        if (isAdvancedWarehouse) {
            loadKpi();
        }
    }, [loadKpi, isAdvancedWarehouse]);

    const runAnalysis = useCallback(async () => {
        if (mode === 'destination' && !targetBinCode.trim()) {
            setError(t('manager.modules.consolidation.errors.missingBin', {}, 'Velg en mål-lokasjon først'));
            return;
        }
        setAnalyzing(true);
        setError(null);
        try {
            const response = await consolidationService.analyze({
                warehouseID,
                mode,
                objective,
                topN,
                includeEstimatedCapacity,
                locationPrefix: locationPrefix.trim() || undefined,
                binCode: mode === 'destination' ? targetBinCode.trim() : undefined
            });
            setAnalysis(response);
            // Forhåndsvelg kun "trygge" forslag: ingen advarsler + observert kapasitet.
            const preselected = new Set<string>();
            response.suggestions.forEach(s => {
                if (s.warnings.length === 0 && s.target.capacityConfidence === 'observed') {
                    preselected.add(suggestionKey(s));
                }
            });
            setSelectedKeys(preselected);
            setExpandedKeys(new Set());
        } catch (err) {
            setError(err instanceof Error ? err.message : t('manager.modules.consolidation.errors.analyze', {}, 'Analyse feilet'));
        } finally {
            setAnalyzing(false);
        }
    }, [warehouseID, mode, objective, topN, includeEstimatedCapacity, locationPrefix, targetBinCode, t]);

    const handleAnalyzeClick = () => {
        setExecuteResult(null);
        runAnalysis();
    };

    const handleModeChange = (newMode: ConsolidationMode) => {
        if (newMode === mode) return;
        setMode(newMode);
        // Resultatene gjelder forrige modus — nullstill.
        setAnalysis(null);
        setSelectedKeys(new Set());
        setExpandedKeys(new Set());
        setExecuteResult(null);
        setError(null);
    };

    const handleToggleSelect = (key: string) => {
        setSelectedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleToggleExpand = (key: string) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSelectAll = (select: boolean) => {
        if (!analysis) return;
        setSelectedKeys(select ? new Set(analysis.suggestions.map(suggestionKey)) : new Set());
    };

    const selectedSummary = useMemo(() => {
        if (!analysis) return { count: 0, binsFreed: 0, moves: 0 };
        const selected = analysis.suggestions.filter(s => selectedKeys.has(suggestionKey(s)));
        return {
            count: selected.length,
            binsFreed: selected.reduce((sum, s) => sum + s.binsFreed, 0),
            moves: selected.reduce((sum, s) => sum + s.sources.length, 0)
        };
    }, [analysis, selectedKeys]);

    const buildExecuteLines = useCallback((): ConsolidationExecuteLine[] => {
        if (!analysis) return [];
        return analysis.suggestions
            .filter(s => selectedKeys.has(suggestionKey(s)))
            .flatMap(s => s.sources.map(src => ({
                fromBinCode: src.fromBinCode,
                toBinCode: s.target.binCode,
                itemNo: s.itemNo,
                quantity: src.quantity
            })));
    }, [analysis, selectedKeys]);

    const handleExecute = async (execution: ConsolidationExecution, planName?: string) => {
        const lines = buildExecuteLines();
        if (lines.length === 0 || executing) return;
        setExecuting(true);
        setError(null);
        setExecuteResult(null);
        try {
            const result = await consolidationService.execute({
                warehouseID,
                execution,
                planName,
                lines
            });
            setExecuteResult(result);
            setShowPlanModal(false);
            // Oppdater KPI og re-kjør analysen — situasjonen på lageret er endret.
            await loadKpi();
            await runAnalysis();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('manager.modules.consolidation.errors.execute', {}, 'Utførelse feilet'));
        } finally {
            setExecuting(false);
        }
    };

    const defaultPlanName = useMemo(() => {
        const d = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const dateStr = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
        return t('manager.modules.consolidation.actions.planNameDefault', { '0': dateStr }, `Konsolidering ${dateStr}`);
    }, [t]);

    const canAnalyze = !analyzing && !executing && (mode !== 'destination' || targetBinCode.trim().length > 0);
    const canExecuteSelection = selectedSummary.count > 0 && !executing && !analyzing;

    const tabs: { key: ConsolidationMode; label: string }[] = [
        { key: 'item', label: t('manager.modules.consolidation.tabs.item', {}, 'Fragmenterte varer') },
        { key: 'source', label: t('manager.modules.consolidation.tabs.source', {}, 'Frigjør lokasjoner') },
        { key: 'destination', label: t('manager.modules.consolidation.tabs.destination', {}, 'Destinasjon-først') }
    ];

    return (
        <div className="flex flex-col h-screen w-full overflow-hidden bg-gray-50">
            {/* Header */}
            <div className="flex items-center justify-between bg-white px-4 py-2 border-b border-gray-200 shadow-sm flex-shrink-0">
                <div className="flex items-center space-x-3">
                    <BackButton onClick={onBack} />
                    <Layers className="w-5 h-5 text-blue-600" />
                    <div>
                        <h1 className="text-lg font-bold text-gray-800">
                            {t('manager.modules.consolidation.title', {}, 'Konsolidering')}
                        </h1>
                        <p className="text-xs text-gray-600">
                            {t('manager.modules.consolidation.subtitle', { '0': warehouseID, '1': companyNo }, `Lager: ${warehouseID} | Selskap: ${companyNo}`)}
                        </p>
                    </div>
                </div>
                <button
                    onClick={loadKpi}
                    disabled={kpiLoading}
                    className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                    <RefreshCw className={`w-4 h-4 ${kpiLoading ? 'animate-spin' : ''}`} />
                    <span>{t('common.buttons.refresh', {}, 'Oppdater')}</span>
                </button>
            </div>

            {/* Feilbanner */}
            {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 py-2 text-sm flex items-start justify-between flex-shrink-0">
                    <div className="flex items-start space-x-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                    <button onClick={() => setError(null)} className="text-xs underline">
                        {t('common.buttons.close', {}, 'Lukk')}
                    </button>
                </div>
            )}

            {/* WMS-vakt: kun for AW-lagre der Compass er master på saldo/lokasjon */}
            {!isAdvancedWarehouse && (
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-lg text-center">
                        <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
                        <div className="font-semibold text-yellow-900 mb-1">
                            {t('manager.modules.consolidation.notAdvancedTitle', {}, 'Ikke tilgjengelig for dette lageret')}
                        </div>
                        <div className="text-sm text-yellow-800">
                            {t('manager.modules.consolidation.notAdvancedDescription', {}, 'Konsolidering er kun tilgjengelig for WMS-lagre der Compass er master på saldo og lokasjoner. Det valgte lageret styres av ERP.')}
                        </div>
                    </div>
                </div>
            )}

            {/* Innhold */}
            {isAdvancedWarehouse && (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* KPI-rad */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-gray-100 border border-gray-200 rounded-lg px-4 py-3">
                        <div className="text-xs text-gray-600">{t('manager.modules.consolidation.kpi.fillRate', {}, 'Fyllingsgrad')}</div>
                        <div className="text-2xl font-bold text-gray-800">
                            {kpi ? `${kpi.fillRatePct.toLocaleString('nb-NO', { maximumFractionDigits: 1 })} %` : '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                            {kpi
                                ? t('manager.modules.consolidation.kpi.fillRateDetail', { '0': kpi.occupiedBins, '1': kpi.totalBins }, `${kpi.occupiedBins} av ${kpi.totalBins} lokasjoner i bruk`)
                                : ' '}
                        </div>
                    </div>
                    <div className="bg-gray-100 border border-gray-200 rounded-lg px-4 py-3">
                        <div className="text-xs text-gray-600">{t('manager.modules.consolidation.kpi.emptyBins', {}, 'Tomme lokasjoner')}</div>
                        <div className="text-2xl font-bold text-gray-800">{kpi ? kpi.emptyBins : '—'}</div>
                        <div className="text-xs text-gray-500">{t('manager.modules.consolidation.kpi.emptyBinsDetail', {}, 'ledige i valgt lager')}</div>
                    </div>
                    <div className="bg-gray-100 border border-gray-200 rounded-lg px-4 py-3">
                        <div className="text-xs text-gray-600">{t('manager.modules.consolidation.kpi.freeable', {}, 'Frigjørbart potensial')}</div>
                        <div className="text-2xl font-bold text-gray-800">
                            {analysis ? analysis.summary.binsFreed : '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                            {analysis
                                ? t('manager.modules.consolidation.kpi.freeableDetail', {}, 'fra siste analyse')
                                : t('manager.modules.consolidation.kpi.freeableNoAnalysis', {}, 'kjør en analyse først')}
                        </div>
                    </div>
                    <div className="bg-gray-100 border border-gray-200 rounded-lg px-4 py-3">
                        <div className="text-xs text-gray-600">{t('manager.modules.consolidation.kpi.active', {}, 'Aktive konsolideringer')}</div>
                        <div className="text-2xl font-bold text-gray-800">{kpi ? kpi.activeConsolidations : '—'}</div>
                        <div className="text-xs text-gray-500">{t('manager.modules.consolidation.kpi.activeDetail', {}, 'pågående flytt')}</div>
                    </div>
                </div>

                {/* Modusfaner + filtre */}
                <div className="bg-white border border-gray-200 rounded-lg">
                    <div className="flex border-b border-gray-200">
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => handleModeChange(tab.key)}
                                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                                    mode === tab.key
                                        ? 'border-blue-600 text-blue-700'
                                        : 'border-transparent text-gray-600 hover:text-gray-800'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="p-4 space-y-3">
                        {/* Destinasjon-først: mål-bin */}
                        {mode === 'destination' && (
                            <div className="flex items-end gap-2">
                                <div className="flex-1 max-w-sm">
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                        {t('manager.modules.consolidation.filters.targetBin', {}, 'Mål-lokasjon')}
                                    </label>
                                    <input
                                        type="text"
                                        value={targetBinCode}
                                        onChange={(e) => setTargetBinCode(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && canAnalyze) handleAnalyzeClick(); }}
                                        placeholder={t('manager.modules.consolidation.filters.targetBinPlaceholder', {}, 'Skriv eller søk mål-bin...')}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <button
                                    onClick={() => setShowLocationSearch(true)}
                                    className="flex items-center space-x-1 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold"
                                >
                                    <MapPin className="w-4 h-4" />
                                    <span>{t('manager.modules.consolidation.filters.searchLocation', {}, 'Søk lokasjon')}</span>
                                </button>
                            </div>
                        )}

                        {/* Filterlinje */}
                        <div className="flex flex-wrap items-end gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                    {t('manager.modules.consolidation.filters.objective', {}, 'Mål')}
                                </label>
                                <select
                                    value={objective}
                                    onChange={(e) => setObjective(e.target.value as ConsolidationObjective)}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                >
                                    <option value="maxFreed">{t('manager.modules.consolidation.filters.objectiveMaxFreed', {}, 'Maks frigjorte bins')}</option>
                                    <option value="minMoves">{t('manager.modules.consolidation.filters.objectiveMinMoves', {}, 'Færrest flytt')}</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                    {t('manager.modules.consolidation.filters.locationPrefix', {}, 'Reol/sone-prefiks')}
                                </label>
                                <input
                                    type="text"
                                    value={locationPrefix}
                                    onChange={(e) => setLocationPrefix(e.target.value)}
                                    placeholder={t('manager.modules.consolidation.filters.locationPrefixPlaceholder', {}, 'F.eks. A-01')}
                                    className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">
                                    {t('manager.modules.consolidation.filters.topN', {}, 'Antall forslag')}
                                </label>
                                <select
                                    value={topN}
                                    onChange={(e) => setTopN(parseInt(e.target.value, 10))}
                                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                >
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <label className="flex items-center space-x-2 pb-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={includeEstimatedCapacity}
                                    onChange={(e) => setIncludeEstimatedCapacity(e.target.checked)}
                                />
                                <span>{t('manager.modules.consolidation.filters.includeEstimated', {}, 'Ta med estimert kapasitet')}</span>
                            </label>
                            <button
                                onClick={handleAnalyzeClick}
                                disabled={!canAnalyze}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                            >
                                {analyzing ? (
                                    <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        <span>{t('manager.modules.consolidation.filters.analyzing', {}, 'Analyserer...')}</span>
                                    </>
                                ) : (
                                    <>
                                        <Search className="w-4 h-4" />
                                        <span>{t('manager.modules.consolidation.filters.analyze', {}, 'Analyser')}</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Analyse-sammendrag */}
                        {analysis && !analyzing && (
                            <div className="text-sm text-gray-700 font-semibold">
                                {t(
                                    'manager.modules.consolidation.filters.summary',
                                    { '0': analysis.summary.groupCount, '1': analysis.summary.binsFreed, '2': analysis.summary.moveCount },
                                    `${analysis.summary.groupCount} forslag · frigjør ${analysis.summary.binsFreed} lokasjoner · ${analysis.summary.moveCount} flytt`
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Resultatpanel etter utførelse */}
                {executeResult && (
                    <ExecuteResultPanel result={executeResult} onDismiss={() => setExecuteResult(null)} />
                )}

                {/* Forslagstabell */}
                {analyzing ? (
                    <div className="bg-white border border-gray-200 rounded-lg p-8 flex items-center justify-center">
                        <Loader className="w-6 h-6 animate-spin text-blue-600" />
                        <span className="ml-3 text-sm text-gray-600">
                            {t('manager.modules.consolidation.filters.analyzing', {}, 'Analyserer...')}
                        </span>
                    </div>
                ) : !analysis ? (
                    <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-500">
                        <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">
                            {t('manager.modules.consolidation.table.noAnalysis', {}, 'Velg modus og klikk «Analyser» for å hente forslag.')}
                        </p>
                    </div>
                ) : analysis.suggestions.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-500">
                        <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">
                            {t('manager.modules.consolidation.table.empty', {}, 'Ingen forslag — juster filtrene og analyser på nytt.')}
                        </p>
                    </div>
                ) : (
                    <SuggestionTable
                        suggestions={analysis.suggestions}
                        selectedKeys={selectedKeys}
                        expandedKeys={expandedKeys}
                        onToggleSelect={handleToggleSelect}
                        onToggleExpand={handleToggleExpand}
                        onSelectAll={handleSelectAll}
                        disabled={executing}
                    />
                )}
            </div>
            )}

            {/* Bunnlinje (sticky) */}
            {isAdvancedWarehouse && analysis && analysis.suggestions.length > 0 && (
                <div className="flex items-center justify-between bg-white border-t border-gray-200 px-4 py-3 flex-shrink-0 shadow-sm">
                    <div className="text-sm text-gray-700 font-semibold">
                        {t(
                            'manager.modules.consolidation.actions.selectedSummary',
                            { '0': selectedSummary.count, '1': selectedSummary.binsFreed, '2': selectedSummary.moves },
                            `${selectedSummary.count} forslag valgt · frigjør ${selectedSummary.binsFreed} lokasjoner · ${selectedSummary.moves} flytt`
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setShowPlanModal(true)}
                            disabled={!canExecuteSelection}
                            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                            title={t('manager.modules.consolidation.actions.addToPlanTooltip', {}, 'Legg valgte forslag som steg i en flytteplan i stedet for å utføre nå')}
                        >
                            <ClipboardList className="w-4 h-4" />
                            <span>
                                {t('manager.modules.consolidation.actions.addToPlan', { '0': selectedSummary.count }, `Legg i plan (${selectedSummary.count})`)}
                            </span>
                        </button>
                        <button
                            onClick={() => handleExecute('immediate')}
                            disabled={!canExecuteSelection}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                        >
                            {executing ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    <span>{t('manager.modules.consolidation.actions.executing', {}, 'Utfører...')}</span>
                                </>
                            ) : (
                                <>
                                    <Play className="w-4 h-4" />
                                    <span>
                                        {t('manager.modules.consolidation.actions.executeNow', { '0': selectedSummary.count }, `Opprett utleggsordre (${selectedSummary.count})`)}
                                    </span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Plannavn-modal */}
            {showPlanModal && (
                <PlanNameModal
                    defaultName={defaultPlanName}
                    selectedCount={selectedSummary.count}
                    isBusy={executing}
                    onClose={() => setShowPlanModal(false)}
                    onConfirm={(planName) => handleExecute('plan', planName)}
                />
            )}

            {/* Lokasjonssøk for destinasjon-først */}
            <LocationSearchModal
                isOpen={showLocationSearch}
                onClose={() => setShowLocationSearch(false)}
                onSelect={(location) => setTargetBinCode(location.binCode)}
                title={t('manager.modules.consolidation.filters.searchLocationTitle', {}, 'Velg mål-lokasjon')}
            />
        </div>
    );
};
